import { c as _c } from "react/compiler-runtime";
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import usePDPPhotos from 'data-hooks/usePDPPhotos/index';
import usePDPCylindoViewer from 'data-hooks/usePDPCylindoViewer';
import useOnEsc from 'hooks/useOnEsc';
import usePDPViewGalleryImageEvent from 'data-hooks/useEvents/usePDPViewGalleryImageEvent';
import usePDPGalleryInteractedEvent from 'data-hooks/useEvents/usePDPGalleryInteractedEvent';
import usePdpSelectedCustomizerTab from 'components/consumer/PDP/state/pdpSelectedCustomizerTab/usePdpSelectedCustomizerTab';
import useRemoveBrokenImageUrls from 'global-state/brokenImageUrls/useRemoveBrokenImageUrls';
import RatioContainer from 'components/consumer/RatioContainer';
import JBProductImage from 'components/essentials/JBImage/JBProductImage';
import Placeholder from 'components/consumer/Placeholder';
import SuspenseWithLoader from 'components/consumer/SuspenseWithLoader';
import PDPCylindo3dViewer from 'components/consumer/PDP/PDPCylindo3dViewer';
import PDPDecideLaterOverlay from 'components/consumer/PDP/PDPDecideLaterOverlay';
import PDPGalleryLabelsAndViewingCount from 'components/consumer/PDP/PDPGalleryLabelsAndViewingCount';
import PDPFavoriteButton from 'components/consumer/PDPFavoriteButton';
import { markLuxPageNavigationEnd } from 'commons/lux';
import IconPlus from 'components/icons/iconPlus';
import IconMinus from 'components/icons/iconMinus';
import IconClose from 'components/icons/iconClose';
import { GALLERY_INTERACTION } from 'data-hooks/useEvents/pdpGalleryEventConstants';
import withImgixWidth from 'commons/withImgixWidth';
import { getMainImageImgixPropsForPhoto } from '../PDPDesktopHeroGallery/helpers/imgix-props';
import PDPDimensionShotViewer from '../PDPDimensionShotViewer';
import PDPProductIntroVideo from '../PDPDesktopHeroGallery/PDPProductIntroVideo';
import PDPGalleryRevampCircleButton from '../PDPDesktopGalleryRevamp/PDPGalleryRevampCircleButton';
import PDPGalleryRevampControlsBar from '../PDPDesktopGalleryRevamp/PDPGalleryRevampControlsBar';
import PDPGalleryRevampSpinZoomHint from '../PDPDesktopGalleryRevamp/PDPGalleryRevampSpinZoomHint';
import clickViewerControl, { closeViewerAR } from '../PDPDesktopGalleryRevamp/clickViewerControl';
import useCylindoARActive from '../hooks/useCylindoARActive';
import useCylindoBootOverlay from '../hooks/useCylindoBootOverlay';
import getMobileGallerySizes from './getMobileGallerySizes';
import PDPMobileGalleryRevampThumbRail from './PDPMobileGalleryRevampThumbRail';
import PDPMobileGalleryRevampExpandedGallery from './PDPMobileGalleryRevampExpandedGallery';
const REVAMP_VIEWER_ID = 'PDPMobileGalleryRevampViewer';
const EMPTY_ARRAY = [];

// Full-bleed on a phone, so a 2x candidate for the widest common viewport
const DIMENSION_SHOT_IMAGE_WIDTH = 900;

// Redesigned mobile gallery (CON-8444), behind ?galleryRevamp=1 for now
const PDPMobileGalleryRevampInner = ({
  slug,
  productHeroImage = null,
  productCylindoHeroImage = null
}) => {
  const {
    loading,
    error,
    data,
    actions
  } = usePDPPhotos(null, slug);
  const pdpSelectedCustomizerTab = usePdpSelectedCustomizerTab();
  const {
    resetGalleryImageTracking,
    viewGalleryImageEvent
  } = usePDPViewGalleryImageEvent();
  const {
    trackGalleryInteracted
  } = usePDPGalleryInteractedEvent();
  const {
    cylindoFeatures,
    isCylindoAvailable,
    cylindoConfigurationPhotos,
    isCylindoConfigurationPhotos,
    isMechanicalOpen,
    toggleMechanicalOpen,
    dimensionShotUrls,
    selectedDimensionShotIndex,
    shouldShowDimensionThumbnail,
    setSelectedDimensionShotIndex
  } = usePDPCylindoViewer({
    // isPDPDesktop only gates the 360 thumb urls + open/close state, not layout
    isPDPDesktop: true,
    deferCylindoFetch: true
  });
  const [isMeasureActive, setIsMeasureActive] = useState(false);
  const [expandedGalleryIndex, setExpandedGalleryIndex] = useState(null);

  // Each opening is its own run of images, so the dedupe starts clean
  useEffect(() => {
    if (expandedGalleryIndex != null) {
      resetGalleryImageTracking();
    }
  }, [expandedGalleryIndex, resetGalleryImageTracking]);
  const {
    isARActive,
    rearm: rearmARWatcher
  } = useCylindoARActive(REVAMP_VIEWER_ID);
  const {
    configurationPhotos: nonCylindoConfigurationPhotos,
    dimensionalImage,
    enableCrops,
    introVideoIds,
    isConfigFlipped,
    isVideoSelected,
    productName,
    selectedPhoto,
    studioPhotos: allStudioPhotos
  } = data ?? {};
  const {
    setSelectedPhoto,
    setIsVideoSelected
  } = actions ?? {};
  const configurationPhotos = useRemoveBrokenImageUrls(isCylindoConfigurationPhotos ? cylindoConfigurationPhotos : nonCylindoConfigurationPhotos);
  const studioPhotos = useRemoveBrokenImageUrls(allStudioPhotos);

  // selectPhoto clears isVideoSelected, so this drops back to the 360 spin
  const closeVideoView = useCallback(() => setSelectedPhoto?.(configurationPhotos[0] ?? null), [configurationPhotos, setSelectedPhoto]);
  const closeMeasureView = useCallback(() => setIsMeasureActive(false), []);
  const escapeTarget = isMeasureActive ? closeMeasureView : closeVideoView;
  useOnEsc((isMeasureActive || !!isVideoSelected) && expandedGalleryIndex == null, escapeTarget);
  const mainPhotoSizes = getMobileGallerySizes();
  const showDecideLaterOverlay = pdpSelectedCustomizerTab === 'decide-later';

  // Falls back to the static dimensional image when there are no Cylindo shots
  const dimensionShotsToShow = useMemo(() => {
    if (dimensionShotUrls.length) {
      return dimensionShotUrls;
    }
    if (dimensionalImage?.src) {
      return [{
        src: withImgixWidth(dimensionalImage.src, DIMENSION_SHOT_IMAGE_WIDTH),
        dimensionCode: 'static',
        id: 'static-dimensional'
      }];
    }
    return EMPTY_ARRAY;
  }, [dimensionShotUrls, dimensionalImage]);
  const introVideoId = introVideoIds?.desktop;
  const showMeasureView = isMeasureActive && !!dimensionShotsToShow.length;
  const showVideoView = !showMeasureView && isVideoSelected && !!introVideoId;
  const showCylindoViewer = isCylindoAvailable && isCylindoConfigurationPhotos && !showMeasureView && !showVideoView;
  const fallbackPhoto = selectedPhoto?.src && selectedPhoto.type !== 'dimensional' ? selectedPhoto : configurationPhotos?.[0] ?? studioPhotos?.[0];

  // The viewer boots behind the photo it replaces. The photo covers it, then
  // fades out once the viewer paints, so the hero never vanishes or jumps size
  // while the viewer starts up (CON-8444).
  const {
    overlayPhoto,
    isViewerReady,
    onViewerLoaded
  } = useCylindoBootOverlay({
    isViewerActive: showCylindoViewer,
    photo: fallbackPhoto
  });
  if (loading) {
    // Prefer Redis hero through Imgix. Cylindo SSR frames must stay as a plain
    // <img> (no Imgix params) to preserve byte-identity / valid LCP URL.
    const redisHeroSrc = productHeroImage?.src || null;
    const cylindoHeroSrc = !redisHeroSrc ? productCylindoHeroImage?.src || null : null;
    // Same framing the loaded branch will use, so the URL doesn't change (and
    // re-fetch) when the photos land (CON-8444)
    const redisHeroPhoto = redisHeroSrc ? {
      type: redisHeroSrc.includes('/configurations/') ? 'configuration' : 'studio',
      src: redisHeroSrc
    } : null;
    return <div className="mb-4 relative" data-xc="PDPMobileGalleryRevamp">
        <RatioContainer>
          {redisHeroPhoto?.src ? <JBProductImage className="h-full w-full object-contain object-center select-none" {...getMainImageImgixPropsForPhoto(redisHeroPhoto)} sizes={mainPhotoSizes} lazy={false} priority /> : cylindoHeroSrc ? <img className="h-full w-full object-contain object-center select-none" src={cylindoHeroSrc} alt={productCylindoHeroImage?.alt || ''} width={productCylindoHeroImage?.width || 800} height={productCylindoHeroImage?.height || 800} loading="eager" fetchPriority="high" decoding="async" /> : <Placeholder type="rect" loading />}
        </RatioContainer>
      </div>;
  }
  if (!data) {
    return null;
  }
  if (error) {
    return 'Something went wrong, please try again';
  }
  const isExpandedGalleryOpen = expandedGalleryIndex != null;
  const heroPhoto = overlayPhoto ?? fallbackPhoto;
  const selectedConfigIndex = Math.max(0, configurationPhotos.findIndex(({
    src
  }) => src === selectedPhoto?.src));
  const isConfigPhotoSelected = selectedPhoto?.type === 'configuration' || selectedPhoto?.type === 'cylindo';
  return <div className="mb-4 relative" data-xc="PDPMobileGalleryRevamp">
      <div className="flex items-center gap-2 px-4 py-2">
        <div className="flex-1 min-w-0">
          <PDPMobileGalleryRevampThumbRail introVideoId={introVideoId} isVideoSelected={showVideoView} onExpand={setExpandedGalleryIndex} onSelectVideo={() => setIsVideoSelected?.()} photos={studioPhotos} trackGalleryInteracted={trackGalleryInteracted} />
        </div>

        <div className={classnames({
        hidden: isARActive
      })}>
          <PDPFavoriteButton />
        </div>
      </div>

      <div className="relative">
        <RatioContainer className={classnames('bg-transparent border-0 focus:outline-0',
      // Cylindo's AR panel ships a slate-blue backdrop; restyle it to Joybird
      // teal and keep its close button visible while our fabs stay hidden
      '[&_cylindo-viewer::part(fab)]:hidden [&_cylindo-viewer::part(qr-fab)]:!flex [&_cylindo-viewer::part(qr-backdrop)]:!bg-[rgba(16,124,140,0.8)] [&_cylindo-viewer::part(qr-dialog)]:border-2 [&_cylindo-viewer::part(qr-dialog)]:border-solid [&_cylindo-viewer::part(qr-dialog)]:border-brand')}>
          {showMeasureView && <>
              <PDPDimensionShotViewer dimensionShots={dimensionShotsToShow} selectedDimensionShotIndex={selectedDimensionShotIndex} onIndexChange={setSelectedDimensionShotIndex} previousArrowClass="left-2" nextArrowClass="right-2" arrowButtonClass="w-9 h-9 bg-white border border-solid border-gray-light6 hover:bg-gray-light9 [&_svg_path]:fill-gray-dark" className="h-full w-full" lazy={false} onLoad={markLuxPageNavigationEnd} />
              <div className="absolute flex flex-col gap-2 right-2 top-4 z-[2]">
                <PDPGalleryRevampCircleButton ariaLabel="Close measurements" onClick={() => setIsMeasureActive(false)}>
                  <IconClose width={9} height={9} />
                </PDPGalleryRevampCircleButton>
              </div>
            </>}

          {showVideoView && <>
              <PDPProductIntroVideo videoId={introVideoId} />
              <div className="absolute flex flex-col gap-2 right-2 top-4 z-[2]">
                <PDPGalleryRevampCircleButton ariaLabel="Close video" onClick={closeVideoView}>
                  <IconClose width={9} height={9} />
                </PDPGalleryRevampCircleButton>
              </div>
            </>}

          {!showMeasureView && !showVideoView && (!showCylindoViewer || !!overlayPhoto) && !!heroPhoto?.src &&
        // JBImage overrides any opacity class on its img once loaded.
        // The overlay and its fade live on this wrapper instead.
        <div className={classnames('h-full w-full', {
          'absolute inset-0 z-[1] pointer-events-none transition-opacity duration-500': !!overlayPhoto,
          'opacity-0': !!overlayPhoto && isViewerReady
        })}>
                <JBProductImage className={classnames('h-full w-full object-contain object-center select-none', {
            'scale-x-[-1]': heroPhoto.type !== 'studio' && isConfigFlipped
          })} {...getMainImageImgixPropsForPhoto(heroPhoto, enableCrops)} sizes={mainPhotoSizes} lazy={false} priority onLoad={markLuxPageNavigationEnd} />
              </div>}

          {isCylindoAvailable && <div className={classnames('w-full h-full [&_.mxt-btn-download]:hidden [&_.mxt-btn-expand]:hidden', {
          invisible: !showCylindoViewer
        })}>
              <PDPCylindo3dViewer containerId={REVAMP_VIEWER_ID} cylindoFeatures={cylindoFeatures} rotateIndex={isConfigPhotoSelected ? selectedConfigIndex : -1} onViewerLoaded={onViewerLoaded} />
            </div>}

          {showCylindoViewer && !isARActive && <PDPGalleryRevampSpinZoomHint />}

          {showDecideLaterOverlay && <PDPDecideLaterOverlay />}
        </RatioContainer>

        <div className="absolute left-4 top-4 z-[2]">
          <PDPGalleryLabelsAndViewingCount mode="pdp" />
        </div>

        {showCylindoViewer && !isARActive && <div className="absolute flex flex-col gap-2 right-4 top-4 z-[2]">
            <PDPGalleryRevampCircleButton ariaLabel="Zoom in" onClick={() => clickViewerControl(REVAMP_VIEWER_ID, 'zoom-in')}>
              <IconPlus width={15} height={15} />
            </PDPGalleryRevampCircleButton>
            <PDPGalleryRevampCircleButton ariaLabel="Zoom out" onClick={() => clickViewerControl(REVAMP_VIEWER_ID, 'zoom-out')}>
              <IconMinus width={15} height={15} />
            </PDPGalleryRevampCircleButton>
          </div>}
      </div>

      <PDPGalleryRevampControlsBar isMeasureActive={showMeasureView}
    // Only one of measure / open can be active, so turning measure on
    // closes the mechanical view the same way opening clears measure
    onToggleMeasure={() => {
      if (!showMeasureView && isMechanicalOpen) {
        toggleMechanicalOpen();
      }
      // Only the off -> on transition counts as "activated" (CON-8512)
      if (!showMeasureView) {
        trackGalleryInteracted(GALLERY_INTERACTION.MEASURE_ACTIVATED);
      }
      setIsMeasureActive(!showMeasureView);
    }} showMeasure={shouldShowDimensionThumbnail} isMechanicalOpen={isMechanicalOpen} toggleMechanicalOpen={() => {
      // Last interaction wins: toggling open/close leaves measure view
      setIsMeasureActive(false);
      // Only the closed -> open transition counts as "activated" (CON-8512)
      if (!isMechanicalOpen) {
        trackGalleryInteracted(GALLERY_INTERACTION.OPEN_STATE_ACTIVATED);
      }
      toggleMechanicalOpen();
    }} isARActive={isARActive} onLaunchViewerAR={isCylindoAvailable && isCylindoConfigurationPhotos ? () => {
      setIsMeasureActive(false);
      clickViewerControl(REVAMP_VIEWER_ID, 'view-in-ar');
      rearmARWatcher();
    } : null} onCloseViewerAR={() => closeViewerAR(REVAMP_VIEWER_ID)} />

      {/* Overlays the rail + viewer + controls area; viewer stays mounted underneath */}
      {isExpandedGalleryOpen && <div className="absolute inset-0 z-[3] bg-gray-light8">
          <PDPMobileGalleryRevampExpandedGallery initialIndex={expandedGalleryIndex} introVideoId={introVideoId} onClose={() => setExpandedGalleryIndex(null)} photos={studioPhotos} productName={productName} trackGalleryInteracted={trackGalleryInteracted} viewGalleryImageEvent={viewGalleryImageEvent} />
        </div>}
    </div>;
};

// Own Suspense boundary: without it a suspending read here bubbles to the PDP
// region boundary and blanks the whole page. This component renders CSS-hidden
// on desktop too (CON-8543).
const PDPMobileGalleryRevamp = props => {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <RatioContainer><Placeholder type="rect" loading={true} /></RatioContainer>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== props) {
    t1 = <SuspenseWithLoader fallback={t0}><PDPMobileGalleryRevampInner {...props} /></SuspenseWithLoader>;
    $[1] = props;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};
PDPMobileGalleryRevamp.propTypes = {
  slug: PropTypes.string,
  productHeroImage: PropTypes.shape({
    src: PropTypes.string.isRequired,
    width: PropTypes.number,
    height: PropTypes.number,
    alt: PropTypes.string
  }),
  productCylindoHeroImage: PropTypes.shape({
    src: PropTypes.string.isRequired,
    width: PropTypes.number,
    height: PropTypes.number,
    alt: PropTypes.string
  })
};
export default PDPMobileGalleryRevamp;